import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Users,
  Wallet,
  AlertTriangle,
  CheckCircle2,
  Smartphone,
  ArrowUpRight,
} from "lucide-react";

import "./styles/clients.css";
import { toast } from "react-toastify";

const API_URL = "https://receivablesbackend.onrender.com/api";

const Clients = () => {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  const [activeFilter, setActiveFilter] = useState("all");

  // ======================================================
  // FETCH DATA
  // ======================================================

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, invoicesRes, paymentsRes] = await Promise.all([
          fetch(`${API_URL}/customers`),
          fetch(`${API_URL}/invoices`),
          fetch(`${API_URL}/payments`),
        ]);

        if (!customersRes.ok || !invoicesRes.ok || !paymentsRes.ok) {
          throw new Error("Failed to load data");
        }

        const customers = await customersRes.json();
        const invoices = await invoicesRes.json();
        const payments = await paymentsRes.json();

        const enrichedClients = customers.map((c) => {
          const clientInvoices = invoices.filter(
            (inv) => Number(inv.pos_customer_id) === Number(c.id)
          );

          const clientPayments = payments.filter(
            (p) => Number(p.pos_customer_id) === Number(c.id)
          );

          const totalInvoiceAmount = clientInvoices.reduce(
            (sum, inv) => sum + Number(inv.amount || 0),
            0
          );

          const totalPayments = clientPayments.reduce(
            (sum, p) => sum + Number(p.amount || 0),
            0
          );

          const balance = totalInvoiceAmount - totalPayments;

          let grade = "N/A";

          if (balance <= 0) {
            grade = "Perfect";
          } else if (balance > 0 && balance <= 50000) {
            grade = "Good";
          } else {
            grade = "Bad";
          }

          return {
            id: c.id,
            full_name: c.name,
            address: c.address || "No Address",
            contact: c.phone || "No Contact",
            balance,
            assessment: grade,
          };
        });

        const sorted = enrichedClients.sort((a, b) =>
          a.full_name.localeCompare(b.full_name)
        );

        setClients(sorted);
        setFilteredClients([]);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load clients");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ======================================================
  // SEARCH
  // ======================================================

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!searchQuery.trim()) {
        setFilteredClients([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      const query = searchQuery.toLowerCase();

      let results = clients.filter((client) => {
        return (
          client.full_name?.toLowerCase().includes(query) ||
          client.contact?.toLowerCase().includes(query) ||
          client.address?.toLowerCase().includes(query)
        );
      });

      // FILTERS

      if (activeFilter === "outstanding") {
        results = results.filter((c) => c.balance > 0);
      }

      if (activeFilter === "perfect") {
        results = results.filter((c) => c.assessment === "Perfect");
      }

      if (activeFilter === "bad") {
        results = results.filter((c) => c.assessment === "Bad");
      }

      setFilteredClients(results);

      setTimeout(() => {
        setIsSearching(false);
      }, 300);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchQuery, activeFilter, clients]);

  // ======================================================
  // STATS
  // ======================================================

  const stats = useMemo(() => {
    return {
      totalClients: clients.length,
      outstanding: clients.filter((c) => c.balance > 0).length,
      perfect: clients.filter((c) => c.assessment === "Perfect").length,
      receivables: clients.reduce((sum, c) => sum + c.balance, 0),
    };
  }, [clients]);

  // ======================================================
  // ACTIONS
  // ======================================================

  const handleClientClick = (id) => {
    navigate(`/clients/${id}`);
  };

  const sendSMS = async (e, clientId, clientName) => {
    e.stopPropagation();

    try {
      const res = await fetch(`${API_URL}/sms/${clientId}`, {
        method: "POST",
      });

      if (!res.ok) throw new Error();

      toast.success(`SMS sent to ${clientName}`);
    } catch (err) {
      toast.error("Failed to send SMS");
    }
  };

  // ======================================================
  // UI
  // ======================================================

  return (
    <div className="modern-clients-page">
      <div className="lookup-container">

        {/* HEADER */}

        <div className="lookup-header">
          <div>
            <h1>Clients Lookup</h1>
            <p>Search and manage receivable customers</p>
          </div>

          <button
            className="pos-btn"
            onClick={() =>
              window.open("https://pos.rcsgy.com/customers", "_blank")
            }
          >
            Managed in POS
          </button>
        </div>

        {/* SEARCH */}

        <div className="search-wrapper">
          <Search className="search-icon" size={22} />

          <input
            type="text"
            placeholder="Search by name, contact, address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="modern-search-input"
          />
        </div>

        {/* FILTERS */}

        <div className="quick-filters">
          <button
            className={activeFilter === "all" ? "active-filter" : ""}
            onClick={() => setActiveFilter("all")}
          >
            All
          </button>

          <button
            className={activeFilter === "outstanding" ? "active-filter" : ""}
            onClick={() => setActiveFilter("outstanding")}
          >
            Outstanding
          </button>

          <button
            className={activeFilter === "perfect" ? "active-filter" : ""}
            onClick={() => setActiveFilter("perfect")}
          >
            Perfect
          </button>

          <button
            className={activeFilter === "bad" ? "active-filter" : ""}
            onClick={() => setActiveFilter("bad")}
          >
            Bad Standing
          </button>
        </div>

        {/* MAIN DISPLAY */}

        <div className="results-container">

          {loading ? (
            <div className="empty-state">
              <div className="loader-ring"></div>
              <h2>Loading Client Database</h2>
            </div>
          ) : !searchQuery ? (
            <div className="empty-state">
              <div className="search-orb">
                <Search size={40} />
              </div>

              <h2>Start typing to search clients</h2>

              <p>
                Search customers by name, contact number, or address
              </p>
            </div>
          ) : isSearching ? (
            <div className="empty-state">
              <div className="loader-ring"></div>
              <h2>Searching Clients...</h2>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="empty-state">
              <AlertTriangle size={40} />
              <h2>No clients found</h2>
            </div>
          ) : (
            <div className="client-grid">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  className="client-card"
                  onClick={() => handleClientClick(client.id)}
                >
                  <div className="client-top">
                    <div className="client-avatar">
                      <Users size={22} />
                    </div>

                    <ArrowUpRight size={18} />
                  </div>

                  <h3>{client.full_name}</h3>

                  <p>{client.address}</p>

                  <span>{client.contact}</span>

                  <div className="client-balance">
                    <Wallet size={18} />

                    <strong
                      style={{
                        color:
                          client.balance > 0 ? "#ef4444" : "#16a34a",
                      }}
                    >
                      ${Number(client.balance).toFixed(2)}
                    </strong>
                  </div>

                  <div
                    className={`assessment-pill ${
                      client.assessment === "Perfect"
                        ? "perfect"
                        : client.assessment === "Good"
                        ? "good"
                        : "bad"
                    }`}
                  >
                    {client.assessment === "Perfect" && (
                      <CheckCircle2 size={14} />
                    )}

                    {client.assessment}
                  </div>

                  <button
                    className="sms-action-btn"
                    onClick={(e) =>
                      sendSMS(e, client.id, client.full_name)
                    }
                  >
                    <Smartphone size={16} />
                    Send SMS
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* STATS */}

        <div className="stats-grid">
          <div className="stat-card">
            <h4>Total Clients</h4>
            <strong>{stats.totalClients}</strong>
          </div>

          <div className="stat-card">
            <h4>Outstanding</h4>
            <strong>{stats.outstanding}</strong>
          </div>

          <div className="stat-card">
            <h4>Perfect Clients</h4>
            <strong>{stats.perfect}</strong>
          </div>

          <div className="stat-card">
            <h4>Total Receivables</h4>
            <strong>
              ${stats.receivables.toLocaleString()}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Clients;